import React, { useEffect, useState } from "react";
import {
  TrendingUp,
  DollarSign,
  ArrowUpCircle,
  ArrowDownCircle,
  Activity,
  Layers,
  RefreshCw,
  Search,
} from "lucide-react";
import api from "../../utils/axios";
import formatCurrency from "../../helper/formatCurrency";
import CustomLoader from "../CustomLoader";

interface ProviderData {
  provider: string;
  total_bets_count: number;
  total_wager: number | string;
  total_win: number | string;
  ggr: number | string;
}

interface GameData {
  game_name: string;
  provider: string;
  total_bets_count: number;
  total_wager: number | string;
  total_win: number | string;
  ggr: number | string;
}

interface TransactionData {
  transaction_id: string | number;
  uid: number;
  amount: number | string;
  req_type: string;
  status: string;
  created_at: string;
}

interface ProfitReport {
  total_deposits: number | string;
  total_withdrawals: number | string;
  net_profit: number | string;
}

const GameAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [providerPerformance, setProviderPerformance] = useState<ProviderData[]>([]);
  const [gamePerformance, setGamePerformance] = useState<GameData[]>([]);
  const [profitReport, setProfitReport] = useState<ProfitReport | null>(null);
  const [transactionHistory, setTransactionHistory] = useState<TransactionData[]>([]);

  // Search filter states
  const [gameSearch, setGameSearch] = useState("");
  const [txSearch, setTxSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/getGameAnalytics");
      if (res.data?.status) {
        const { providerPerformance, gamePerformance, profitReport, transactionHistory } = res.data.data;
        setProviderPerformance(providerPerformance || []);
        setGamePerformance(gamePerformance || []);
        setProfitReport(profitReport || null);
        setTransactionHistory(transactionHistory || []);
      } else {
        setError("Failed to retrieve analytics data.");
      }
    } catch (err) {
      console.error("Error loading game analytics:", err);
      setError("An error occurred while loading analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <CustomLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 font-semibold">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          Retry
        </button>
      </div>
    );
  }

  // Calculate gross provider wagers and wins to compute overall GGR
  const totalProviderWagers = providerPerformance.reduce(
    (sum, p) => sum + Number(p.total_wager || 0),
    0
  );
  const totalProviderWins = providerPerformance.reduce(
    (sum, p) => sum + Number(p.total_win || 0),
    0
  );
  const overallGGR = totalProviderWagers - totalProviderWins;

  // Filtered Lists
  const filteredGames = gamePerformance.filter(
    (g) =>
      String(g.game_name || "").toLowerCase().includes(gameSearch.toLowerCase()) ||
      String(g.provider || "").toLowerCase().includes(gameSearch.toLowerCase())
  );

  const filteredTx = transactionHistory.filter(
    (t) =>
      String(t.transaction_id ?? "").toLowerCase().includes(txSearch.toLowerCase()) ||
      String(t.uid ?? "").includes(txSearch) ||
      String(t.req_type || "").toLowerCase().includes(txSearch.toLowerCase()) ||
      String(t.status || "").toLowerCase().includes(txSearch.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* ================= HEADER ================= */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">
            Game Analytics & Business Reports
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            GGR performance, provider wagers, game margins, and financial reports
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition shadow-md"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* ================= METRIC CARDS ================= */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Gross Gaming Revenue Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute right-4 top-4 bg-purple-50 text-purple-600 p-3 rounded-xl">
            <Activity size={24} />
          </div>
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
            Total GGR
          </p>
          <p className="text-3xl font-bold text-slate-800 mt-2">
            {formatCurrency(overallGGR)}
          </p>
          <p className="text-xs text-slate-400 mt-2">Total Bets - Total Wins</p>
        </div>

        {/* Profit Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute right-4 top-4 bg-emerald-50 text-emerald-600 p-3 rounded-xl">
            <TrendingUp size={24} />
          </div>
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
            Net Profit
          </p>
          <p className="text-3xl font-bold text-slate-800 mt-2">
            {formatCurrency(Number(profitReport?.net_profit || 0))}
          </p>
          <p className="text-xs text-slate-400 mt-2">Deposits - Withdrawals</p>
        </div>

        {/* Deposits Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute right-4 top-4 bg-blue-50 text-blue-600 p-3 rounded-xl">
            <ArrowUpCircle size={24} />
          </div>
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
            Total Deposits
          </p>
          <p className="text-3xl font-bold text-slate-800 mt-2">
            {formatCurrency(Number(profitReport?.total_deposits || 0))}
          </p>
          <p className="text-xs text-slate-400 mt-2">Approved player deposits</p>
        </div>

        {/* Withdrawals Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute right-4 top-4 bg-rose-50 text-rose-600 p-3 rounded-xl">
            <ArrowDownCircle size={24} />
          </div>
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
            Total Withdrawals
          </p>
          <p className="text-3xl font-bold text-slate-800 mt-2">
            {formatCurrency(Number(profitReport?.total_withdrawals || 0))}
          </p>
          <p className="text-xs text-slate-400 mt-2">Approved player payouts</p>
        </div>
      </div>

      {/* ================= PROVIDER PERFORMANCE ================= */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50/50 flex justify-between items-center">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Layers className="text-purple-600" size={20} />
            Game Provider Performance
          </h2>
          <span className="text-xs font-semibold px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full">
            All Platforms
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-slate-700">
            <thead className="bg-slate-100 text-slate-600 uppercase text-xs font-bold border-b">
              <tr>
                <th className="px-6 py-4">Provider</th>
                <th className="px-6 py-4">Bets Volume</th>
                <th className="px-6 py-4">Total Wagered</th>
                <th className="px-6 py-4">Total Payouts</th>
                <th className="px-6 py-4 text-right">GGR</th>
                <th className="px-6 py-4 text-right">House Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {providerPerformance.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-400">
                    No provider bet history found.
                  </td>
                </tr>
              ) : (
                providerPerformance.map((p, index) => {
                  const wager = Number(p.total_wager || 0);
                  const win = Number(p.total_win || 0);
                  const ggr = Number(p.ggr || 0);
                  const houseMargin = wager > 0 ? ((ggr / wager) * 100).toFixed(2) : "0.00";

                  return (
                    <tr key={index} className="hover:bg-slate-50 transition">
                      <td className="px-6 py-4 font-semibold text-slate-800">
                        {p.provider}
                      </td>
                      <td className="px-6 py-4">{p.total_bets_count}</td>
                      <td className="px-6 py-4">{formatCurrency(wager)}</td>
                      <td className="px-6 py-4">{formatCurrency(win)}</td>
                      <td className={`px-6 py-4 text-right font-bold ${ggr >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {formatCurrency(ggr)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-slate-600">
                        {houseMargin}%
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= INDIVIDUAL GAME PERFORMANCE ================= */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50/50 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <DollarSign className="text-blue-600" size={20} />
              Individual Game Performance
            </h2>
            <p className="text-xs text-slate-400 mt-1">Top 50 games by wagering volume</p>
          </div>
          <div className="relative max-w-md w-full">
            <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search game or provider..."
              value={gameSearch}
              onChange={(e) => setGameSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm"
            />
          </div>
        </div>
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm text-left text-slate-700">
            <thead className="bg-slate-100 text-slate-600 uppercase text-xs font-bold border-b sticky top-0 z-10">
              <tr>
                <th className="px-6 py-4 bg-slate-100">Game Code</th>
                <th className="px-6 py-4 bg-slate-100">Provider</th>
                <th className="px-6 py-4 bg-slate-100">Bets Count</th>
                <th className="px-6 py-4 bg-slate-100">Total Wager</th>
                <th className="px-6 py-4 bg-slate-100">Total Win</th>
                <th className="px-6 py-4 bg-slate-100 text-right">GGR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredGames.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-400">
                    No matching game records found.
                  </td>
                </tr>
              ) : (
                filteredGames.map((g, index) => {
                  const wager = Number(g.total_wager || 0);
                  const win = Number(g.total_win || 0);
                  const ggr = Number(g.ggr || 0);

                  return (
                    <tr key={index} className="hover:bg-slate-50 transition">
                      <td className="px-6 py-4 font-mono text-slate-800 text-xs">
                        {g.game_name}
                      </td>
                      <td className="px-6 py-4 text-xs font-medium text-slate-500">
                        {g.provider}
                      </td>
                      <td className="px-6 py-4">{g.total_bets_count}</td>
                      <td className="px-6 py-4">{formatCurrency(wager)}</td>
                      <td className="px-6 py-4">{formatCurrency(win)}</td>
                      <td className={`px-6 py-4 text-right font-bold ${ggr >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {formatCurrency(ggr)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= TRANSACTION HISTORY & LOG ================= */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50/50 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Activity className="text-emerald-600" size={20} />
              Recent Transaction History
            </h2>
            <p className="text-xs text-slate-400 mt-1">Audit log of deposit & withdrawal requests</p>
          </div>
          <div className="relative max-w-md w-full">
            <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search UID, Status, Type..."
              value={txSearch}
              onChange={(e) => setTxSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-slate-700">
            <thead className="bg-slate-100 text-slate-600 uppercase text-xs font-bold border-b">
              <tr>
                <th className="px-6 py-4">Transaction ID</th>
                <th className="px-6 py-4">User UID</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredTx.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-400">
                    No transactions found.
                  </td>
                </tr>
              ) : (
                filteredTx.map((t, index) => (
                  <tr key={index} className="hover:bg-slate-50 transition">
                    <td className="px-6 py-4 font-mono text-xs text-slate-800">
                      {t.transaction_id}
                    </td>
                    <td className="px-6 py-4">UID: {t.uid}</td>
                    <td className="px-6 py-4 font-semibold text-slate-800">
                      {formatCurrency(Number(t.amount))}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full uppercase ${t.req_type === "deposit" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>
                        {t.req_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full uppercase ${t.status === "accepted" ? "bg-emerald-50 text-emerald-700" : t.status === "pending" ? "bg-yellow-50 text-yellow-700" : "bg-red-50 text-red-700"}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-500">
                      {t.created_at ? new Date(t.created_at).toLocaleString() : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GameAnalytics;
